import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, DollarSign, Clock, Shield, MapPin, Users, CheckCircle, Upload, Send, FileText, Mail } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cdl_type: '',
    years_experience: '',
    message: '',
  });
  const [resume, setResume] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const benefits = [
    {
      icon: DollarSign,
      title: 'Competitive Pay',
      description: 'Earn what you deserve with competitive rates and consistent work.',
      color: 'red',
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible scheduling options.',
      color: 'blue',
    },
    {
      icon: Shield,
      title: 'Reliable Equipment',
      description: 'Drive well-maintained trucks you can count on.',
      color: 'red',
    },
    {
      icon: MapPin,
      title: 'Local & Regional',
      description: 'Routes that get you home. Based in Augusta, GA.',
      color: 'blue',
    },
    {
      icon: Users,
      title: 'Supportive Team',
      description: 'Join a team that values communication and respect.',
      color: 'red',
    },
    {
      icon: Truck,
      title: 'Growth Opportunity',
      description: 'Build your career with a growing logistics company.',
      color: 'blue',
    },
  ];

  const requirements = [
    'Valid Driver\'s License (No CDL Required)',
    'Clean driving record',
    'Ability to pass DOT physical and drug screening',
    'Strong work ethic and reliability',
    'Good communication skills',
    'Experience with box trucks preferred',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File too large. Maximum size is 5MB.');
        return;
      }
      setResume(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.cdl_type || !formData.years_experience) {
      setSubmitStatus({
        type: 'error',
        message: 'Please select your CDL type and years of experience.',
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('cdl_type', formData.cdl_type);
      submitData.append('years_experience', formData.years_experience);
      submitData.append('message', formData.message || '');
      if (resume) {
        submitData.append('resume', resume);
      }

      const response = await axios.post(`${API}/careers/apply`, submitData);

      setSubmitStatus({ type: 'success', message: response.data.message });
      setFormData({
        name: '',
        email: '',
        phone: '',
        cdl_type: '',
        years_experience: '',
        message: '',
      });
      setResume(null);
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-texture min-h-screen" data-testid="careers-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-brand-surface border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              JOIN THE <span style={{ color: '#DC2626' }}>TOP</span><span className="text-white">FLEET</span> TEAM
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Drive your career forward with a company that values reliability, respect, and results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Drive With Us */}
      <section className="py-24 md:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-4">
              <span className="text-white">WHY DRIVE WITH </span>
              <span style={{ color: '#DC2626' }}>TOP</span>
              <span className="text-white">FLEET</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              We're not just looking for drivers—we're building a team of professionals who take pride in their work.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="service-card rounded-xl border border-white/10 bg-brand-surface p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`icon-container ${benefit.color} mx-auto mb-6`}>
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements & Application Form */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="application-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Requirements */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl text-white mb-8">
                WHAT WE'RE <span style={{ color: '#DC2626' }}>LOOKING FOR</span>
              </h2>
              
              <ul className="space-y-4 mb-12">
                {requirements.map((req, index) => (
                  <li key={`req-${req.slice(0, 15).replace(/\s+/g, '-')}`} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${index % 2 === 0 ? 'text-brand-red' : 'text-brand-blue'}`} />
                    <span className="text-slate-300">{req}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-brand-navy rounded-xl border border-white/10 p-8">
                <h3 className="font-heading text-xl text-white mb-4">READY TO HIT THE ROAD?</h3>
                <p className="text-slate-400 mb-4">
                  Fill out the application form and we'll be in touch within 48 hours to discuss opportunities.
                </p>
                <p className="text-slate-500 text-sm">
                  Questions? Call us at <a href="tel:224-374-7400" className="text-brand-red hover:underline">224-374-7400</a>
                </p>
              </div>

              {/* Direct careers-team contact line (Google Workspace) */}
              <div
                className="mt-6 rounded-xl border border-brand-red/20 bg-brand-red/5 p-6 flex items-start gap-4"
                data-testid="careers-direct-contact"
              >
                <Mail className="w-5 h-5 text-brand-red flex-shrink-0 mt-1" />
                <div className="text-sm text-slate-300 leading-relaxed">
                  <p className="font-semibold text-white mb-1">Interested in joining the TopFleet team?</p>
                  <p className="mb-2 text-slate-400">For driver opportunities, partnerships, or logistics staffing inquiries, contact us at:</p>
                  <a
                    href="mailto:careers@topfleetlogistics.com"
                    className="font-semibold text-brand-red hover:underline break-all"
                    data-testid="careers-direct-email"
                  >
                    careers@topfleetlogistics.com
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Application Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-brand-navy rounded-xl border border-white/10 p-8">
                <h2 className="font-heading text-2xl text-white mb-8">
                  APPLY <span style={{ color: '#DC2626' }}>NOW</span>
                </h2>

                {submitStatus?.type === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="application-success"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                    <h3 className="font-heading text-2xl text-white mb-4">Application Submitted!</h3>
                    <p className="text-slate-400">{submitStatus.message}</p>
                    <button
                      onClick={() => setSubmitStatus(null)}
                      className="btn-secondary mt-8"
                    >
                      Submit Another Application
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="career-form">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                        data-testid="career-name-input"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          required
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="career-email-input"
                        />
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          required
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red"
                          data-testid="career-phone-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* CDL Type */}
                      <div className="space-y-2">
                        <Label className="text-slate-300">CDL Type *</Label>
                        <Select 
                          onValueChange={(value) => handleSelectChange('cdl_type', value)} 
                          value={formData.cdl_type}
                          required
                        >
                          <SelectTrigger 
                            className="bg-white/5 border-white/10 text-white focus:border-brand-red focus:ring-brand-red"
                            data-testid="career-cdl-select"
                          >
                            <SelectValue placeholder="Select CDL type" />
                          </SelectTrigger>
                          <SelectContent className="bg-brand-surface border-white/10">
                            <SelectItem value="none" className="text-white hover:bg-white/10">No CDL</SelectItem>
                            <SelectItem value="class_a" className="text-white hover:bg-white/10">Class A CDL</SelectItem>
                            <SelectItem value="class_b" className="text-white hover:bg-white/10">Class B CDL</SelectItem>
                            <SelectItem value="class_c" className="text-white hover:bg-white/10">Class C CDL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Years Experience */}
                      <div className="space-y-2">
                        <Label className="text-slate-300">Driving Experience *</Label>
                        <Select 
                          onValueChange={(value) => handleSelectChange('years_experience', value)} 
                          value={formData.years_experience}
                          required
                        >
                          <SelectTrigger 
                            className="bg-white/5 border-white/10 text-white focus:border-brand-red focus:ring-brand-red"
                            data-testid="career-experience-select"
                          >
                            <SelectValue placeholder="Years of experience" />
                          </SelectTrigger>
                          <SelectContent className="bg-brand-surface border-white/10">
                            <SelectItem value="0-1" className="text-white hover:bg-white/10">Less than 1 year</SelectItem>
                            <SelectItem value="1-3" className="text-white hover:bg-white/10">1-3 years</SelectItem>
                            <SelectItem value="3-5" className="text-white hover:bg-white/10">3-5 years</SelectItem>
                            <SelectItem value="5-10" className="text-white hover:bg-white/10">5-10 years</SelectItem>
                            <SelectItem value="10+" className="text-white hover:bg-white/10">10+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Resume Upload */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Resume / Credentials (Optional)</Label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                          id="resume-upload"
                          data-testid="career-resume-input"
                        />
                        <label
                          htmlFor="resume-upload"
                          className="flex items-center justify-center gap-3 w-full p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-brand-red/50 transition-colors"
                        >
                          {resume ? (
                            <>
                              <FileText className="w-5 h-5 text-brand-red" />
                              <span className="text-white">{resume.name}</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="text-slate-400">Upload resume or credentials (PDF, DOC, JPG)</span>
                            </>
                          )}
                        </label>
                      </div>
                      <p className="text-slate-500 text-xs">Max file size: 5MB</p>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">Additional Information</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about yourself, your experience, or any questions you have..."
                        rows={4}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-brand-red focus:ring-brand-red resize-none"
                        data-testid="career-message-input"
                      />
                    </div>

                    {submitStatus?.type === 'error' && (
                      <p className="text-red-400 text-sm" data-testid="career-error">
                        {submitStatus.message}
                      </p>
                    )}

                    {/* Consent Checkbox */}
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="career-consent-label">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-brand-red focus:ring-brand-red/50 cursor-pointer"
                        data-testid="career-consent-checkbox"
                        required
                      />
                      <span className="text-slate-400 text-sm leading-relaxed">
                        I agree to the{' '}
                        <Link to="/legal" className="text-brand-red hover:underline" target="_blank">Legal & Policies</Link>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-cta w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="career-submit-button"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Application
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="careers-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              QUESTIONS ABOUT <span style={{ color: '#DC2626' }}>JOINING</span>?
            </h2>
            <p className="text-xl text-slate-400 mb-10">
              Call us directly at <a href="tel:224-374-7400" className="text-brand-red hover:underline font-semibold">224-374-7400</a> to speak with our team.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
